// web/src/crypto/keys.ts — per-participant key manager + fingerprint (M1a.3) and
// the ECDSA P-256 SIGNING keypair (M2.7 / ADR-003 D5).
//
// Zero-trust key custody (ADR-001, R11): each participant holds one ECDH P-256
// keypair whose PRIVATE key is a non-extractable WebCrypto CryptoKey persisted to
// IndexedDB. The private key handle is never serialized off-device — IndexedDB can
// store live CryptoKey objects, so a key generated with `extractable:false` stays
// non-extractable even across reloads. Only the public JWK + its fingerprint leave
// the device.
//
// SIGNING key (ADR-003 D4/D5, A4): a SEPARATE ECDSA P-256 keypair, distinct from
// the ECDH decryption key, used for attestation/assertion and possession-proof
// signatures over a server nonce. ECDSA keys sign/verify only — they cannot decrypt
// any shard, so this keeps the ECDH key single-purpose (decryption) while giving a
// first-class signing primitive. Like the ECDH key the private half is generated
// `extractable:false` and persisted to IndexedDB; only the public JWK + signatures
// over PUBLIC nonces ever leave the device.
//
// The fingerprint / safety-number (contract §7) is derived here from the SPKI-DER
// of the public key: SHA-256 → hex (grouped) + a word/emoji safety-number for
// out-of-band verification. The exact wordlist is deferred to M2.5; we ship a
// small built-in list that fixes the *shape* (6 words / 6 emoji) the contract
// promises. The PublicKeyId is the leading bytes of that same SHA-256 digest, so
// keyId and fingerprint are derived from one hash (contract: "= its fingerprint
// hash, hex").

import { CryptoError } from './types';
import type {
  Base64Url,
  Fingerprint,
  FingerprintCodec,
  KeyManager,
  KeyPairRecord,
  PrivateKeyHandle,
  PublicKeyId,
  PublicKeyJwk,
} from './types';
import { base64UrlToBytes, bytesToBase64Url, jwkToSpkiDer } from './shards';

// ---------------------------------------------------------------------------
// IndexedDB store for non-extractable key handles
// ---------------------------------------------------------------------------

const DB_NAME = 'breakglass-keys';
const DB_VERSION = 1;
const STORE = 'keypairs';

/** Persisted shape. `privateKey` is a live, non-extractable CryptoKey. */
interface StoredKeyPair {
  keyId: PublicKeyId;
  publicKey: PublicKeyJwk;
  privateKey: CryptoKey; // extractable:false — IndexedDB structured-clones the handle
  createdAt: number;
}

function openDb(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined' || indexedDB === null) {
    return Promise.reject(
      new CryptoError('idb_unavailable', 'IndexedDB is not available in this runtime'),
    );
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'keyId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () =>
      reject(new CryptoError('idb_unavailable', String(req.error?.message ?? 'idb open failed')));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () =>
      reject(new CryptoError('idb_unavailable', String(tx.error?.message ?? 'idb tx failed')));
  });
}

function reqResult<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () =>
      reject(new CryptoError('idb_unavailable', String(req.error?.message ?? 'idb request failed')));
  });
}

// ---------------------------------------------------------------------------
// Key-id / fingerprint derivation (shared by KeyManager + FingerprintCodec)
// ---------------------------------------------------------------------------

/** SHA-256 over the canonical SPKI-DER of the public key. */
async function publicKeyDigest(publicKey: PublicKeyJwk): Promise<Uint8Array> {
  const spki = jwkToSpkiDer(publicKey);
  const digest = await crypto.subtle.digest('SHA-256', spki as unknown as ArrayBuffer);
  return new Uint8Array(digest);
}

function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/**
 * Built-in placeholder safety-word + emoji lists (M2.5 will replace the source).
 * 256 entries each so one digest byte maps to exactly one word/emoji. The contract
 * only fixes the *shape* (a word list + a mirroring emoji list); the exact list is
 * a content/design decision deferred to M2.5.
 */
const SAFETY_WORDS: string[] = Array.from({ length: 256 }, (_, i) => `word${i.toString(16).padStart(2, '0')}`);
const SAFETY_EMOJI: string[] = (() => {
  // 256 distinct emoji from a contiguous unicode block (start at U+1F300).
  const base = 0x1f300;
  return Array.from({ length: 256 }, (_, i) => String.fromCodePoint(base + i));
})();

const SAFETY_LENGTH = 6; // 6 words / 6 emoji (ADR-001 open question 2 — shape only)

/** Lowercase hex grouped in 4-char blocks for display. */
function groupHex(hex: string): string {
  return hex.match(/.{1,4}/g)?.join(' ') ?? hex;
}

async function deriveFingerprint(publicKey: PublicKeyJwk): Promise<Fingerprint> {
  const digest = await publicKeyDigest(publicKey);
  const hex = toHex(digest);
  const keyId = hex as PublicKeyId;
  const words: string[] = [];
  const emoji: string[] = [];
  for (let i = 0; i < SAFETY_LENGTH; i++) {
    const byte = digest[i];
    words.push(SAFETY_WORDS[byte]);
    emoji.push(SAFETY_EMOJI[byte]);
  }
  return { keyId, hex: groupHex(hex), words, emoji };
}

/** PublicKeyId is the full SHA-256(SPKI-DER) in lowercase hex. */
async function deriveKeyIdFromKey(publicKey: PublicKeyJwk): Promise<PublicKeyId> {
  const digest = await publicKeyDigest(publicKey);
  return toHex(digest) as PublicKeyId;
}

// ---------------------------------------------------------------------------
// Public-key export helpers
// ---------------------------------------------------------------------------

/** Export a WebCrypto public CryptoKey to the wire JWK shape (contract §2). */
async function exportPublicJwk(key: CryptoKey): Promise<PublicKeyJwk> {
  const jwk = await crypto.subtle.exportKey('jwk', key);
  if (jwk.kty !== 'EC' || jwk.crv !== 'P-256' || !jwk.x || !jwk.y) {
    throw new CryptoError('keygen_failed', 'exported public key is not P-256 EC');
  }
  return {
    kty: 'EC',
    crv: 'P-256',
    x: jwk.x,
    y: jwk.y,
    ext: true,
    key_ops: [],
  };
}

// ---------------------------------------------------------------------------
// KeyManager implementation
// ---------------------------------------------------------------------------

class WebCryptoKeyManager implements KeyManager {
  async generateKeyPair(): Promise<KeyPairRecord> {
    let pair: CryptoKeyPair;
    try {
      pair = (await crypto.subtle.generateKey(
        { name: 'ECDH', namedCurve: 'P-256' },
        false, // private key NON-extractable (R11/M1a.3)
        ['deriveBits'],
      )) as CryptoKeyPair;
    } catch (e) {
      throw new CryptoError('keygen_failed', String((e as Error)?.message ?? e));
    }

    const publicKey = await exportPublicJwk(pair.publicKey);
    const keyId = await deriveKeyIdFromKey(publicKey);
    const createdAt = Date.now();

    const record: StoredKeyPair = {
      keyId,
      publicKey,
      privateKey: pair.privateKey, // non-extractable handle, structured-cloned by IDB
      createdAt,
    };

    const db = await openDb();
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(record);
      await txDone(tx);
    } finally {
      db.close();
    }

    return { keyId, publicKey, createdAt };
  }

  async getPrivateKey(keyId: PublicKeyId): Promise<PrivateKeyHandle> {
    const db = await openDb();
    try {
      const tx = db.transaction(STORE, 'readonly');
      const record = await reqResult<StoredKeyPair | undefined>(
        tx.objectStore(STORE).get(keyId) as IDBRequest<StoredKeyPair | undefined>,
      );
      await txDone(tx);
      if (!record) {
        throw new CryptoError('keygen_failed', `no private key stored for keyId ${keyId}`);
      }
      return { keyId: record.keyId, cryptoKey: record.privateKey };
    } finally {
      db.close();
    }
  }

  async listLocalKeys(): Promise<KeyPairRecord[]> {
    const db = await openDb();
    try {
      const tx = db.transaction(STORE, 'readonly');
      const all = await reqResult<StoredKeyPair[]>(
        tx.objectStore(STORE).getAll() as IDBRequest<StoredKeyPair[]>,
      );
      await txDone(tx);
      return all.map((r) => ({ keyId: r.keyId, publicKey: r.publicKey, createdAt: r.createdAt }));
    } finally {
      db.close();
    }
  }

  async deriveKeyId(publicKey: PublicKeyJwk): Promise<PublicKeyId> {
    return deriveKeyIdFromKey(publicKey);
  }

  async deleteKeyPair(keyId: PublicKeyId): Promise<void> {
    const db = await openDb();
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(keyId);
      await txDone(tx);
    } finally {
      db.close();
    }
  }
}

// ---------------------------------------------------------------------------
// FingerprintCodec implementation
// ---------------------------------------------------------------------------

class WebCryptoFingerprintCodec implements FingerprintCodec {
  derive(publicKey: PublicKeyJwk): Promise<Fingerprint> {
    return deriveFingerprint(publicKey);
  }

  /** Constant-time comparison of the two keyIds (equal-length lowercase hex). */
  equals(a: Fingerprint, b: Fingerprint): boolean {
    return constantTimeEqualStr(a.keyId, b.keyId);
  }
}

/** Length-independent constant-time string compare (avoids early-exit timing leak). */
function constantTimeEqualStr(a: string, b: string): boolean {
  const enc = new TextEncoder();
  const ab = enc.encode(a);
  const bb = enc.encode(b);
  // Compare against the max length so the loop count never reveals which differs.
  const len = Math.max(ab.length, bb.length);
  let diff = ab.length ^ bb.length;
  for (let i = 0; i < len; i++) {
    diff |= (ab[i] ?? 0) ^ (bb[i] ?? 0);
  }
  return diff === 0;
}

// ---------------------------------------------------------------------------
// ECDSA P-256 signing keypair (ADR-003 D4/D5, A4)
// ---------------------------------------------------------------------------
//
// Distinct from the ECDH decryption key. The private half is non-extractable and
// persisted to its own IndexedDB store; the public JWK is exported for the
// registry, and `signNonce` produces an ECDSA(SHA-256) signature over the EXACT
// UTF-8 bytes of a server nonce (attestation/possession-proof, H1). The signature
// is base64url-encoded raw r||s, which `crypto.subtle.verify` accepts server-side.

const SIGN_STORE = 'signing-keypairs';
const SIGN_DB_VERSION = 2; // bumps the shared DB to add the signing store

/** Persisted signing keypair. Both keys are live CryptoKeys; private is non-extractable. */
interface StoredSigningKeyPair {
  keyId: PublicKeyId;
  publicKey: PublicKeyJwk;
  privateKey: CryptoKey; // extractable:false
  createdAt: number;
}

/** A signing public-key record (mirrors KeyPairRecord, for the registry/picker). */
export interface SigningKeyRecord {
  keyId: PublicKeyId;
  publicKey: PublicKeyJwk;
  createdAt: number;
}

/**
 * Manages the per-participant ECDSA P-256 signing keypair. Mirrors KeyManager's
 * custody guarantees (non-extractable private key in IndexedDB) for a key whose
 * single purpose is signing — never decryption (ADR-003 A4).
 */
export interface SigningKeyManager {
  /** Generate the ECDSA P-256 signing keypair; private key extractable:false, persisted. */
  generateSigningKeyPair(): Promise<SigningKeyRecord>;
  /** List signing public-key records held on this device. */
  listSigningKeys(): Promise<SigningKeyRecord[]>;
  /**
   * Sign the EXACT UTF-8 bytes of `nonce` with the stored signing private key.
   * Returns the ECDSA(SHA-256) signature (raw r||s) as base64url — the form the
   * server verifies over the public JWK (ADR-003 D5, H1). Throws if the keyId is absent.
   */
  signNonce(keyId: PublicKeyId, nonce: string): Promise<Base64Url>;
  /** Delete a local signing keypair (lost-device / re-key). Irreversible. */
  deleteSigningKeyPair(keyId: PublicKeyId): Promise<void>;
}

/** Open the keys DB at the version that includes BOTH stores (ECDH + signing). */
function openSignDb(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined' || indexedDB === null) {
    return Promise.reject(
      new CryptoError('idb_unavailable', 'IndexedDB is not available in this runtime'),
    );
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, SIGN_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'keyId' });
      }
      if (!db.objectStoreNames.contains(SIGN_STORE)) {
        db.createObjectStore(SIGN_STORE, { keyPath: 'keyId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () =>
      reject(new CryptoError('idb_unavailable', String(req.error?.message ?? 'idb open failed')));
  });
}

/** Export an ECDSA public CryptoKey to the wire JWK shape (key_ops empty per contract). */
async function exportSigningPublicJwk(key: CryptoKey): Promise<PublicKeyJwk> {
  const jwk = await crypto.subtle.exportKey('jwk', key);
  if (jwk.kty !== 'EC' || jwk.crv !== 'P-256' || !jwk.x || !jwk.y) {
    throw new CryptoError('keygen_failed', 'exported signing key is not P-256 EC');
  }
  return { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y, ext: true, key_ops: [] };
}

class WebCryptoSigningKeyManager implements SigningKeyManager {
  async generateSigningKeyPair(): Promise<SigningKeyRecord> {
    let pair: CryptoKeyPair;
    try {
      pair = (await crypto.subtle.generateKey(
        { name: 'ECDSA', namedCurve: 'P-256' },
        false, // private signing key NON-extractable (R11 custody parity)
        ['sign', 'verify'],
      )) as CryptoKeyPair;
    } catch (e) {
      throw new CryptoError('keygen_failed', String((e as Error)?.message ?? e));
    }

    const publicKey = await exportSigningPublicJwk(pair.publicKey);
    const keyId = await deriveKeyIdFromKey(publicKey);
    const createdAt = Date.now();

    const record: StoredSigningKeyPair = { keyId, publicKey, privateKey: pair.privateKey, createdAt };
    const db = await openSignDb();
    try {
      const tx = db.transaction(SIGN_STORE, 'readwrite');
      tx.objectStore(SIGN_STORE).put(record);
      await txDone(tx);
    } finally {
      db.close();
    }
    return { keyId, publicKey, createdAt };
  }

  async listSigningKeys(): Promise<SigningKeyRecord[]> {
    const db = await openSignDb();
    try {
      const tx = db.transaction(SIGN_STORE, 'readonly');
      const all = await reqResult<StoredSigningKeyPair[]>(
        tx.objectStore(SIGN_STORE).getAll() as IDBRequest<StoredSigningKeyPair[]>,
      );
      await txDone(tx);
      return all.map((r) => ({ keyId: r.keyId, publicKey: r.publicKey, createdAt: r.createdAt }));
    } finally {
      db.close();
    }
  }

  async signNonce(keyId: PublicKeyId, nonce: string): Promise<Base64Url> {
    const db = await openSignDb();
    let record: StoredSigningKeyPair | undefined;
    try {
      const tx = db.transaction(SIGN_STORE, 'readonly');
      record = await reqResult<StoredSigningKeyPair | undefined>(
        tx.objectStore(SIGN_STORE).get(keyId) as IDBRequest<StoredSigningKeyPair | undefined>,
      );
      await txDone(tx);
    } finally {
      db.close();
    }
    if (!record) {
      throw new CryptoError('keygen_failed', `no signing key stored for keyId ${keyId}`);
    }
    const bytes = new TextEncoder().encode(nonce);
    const sig = await crypto.subtle.sign(
      { name: 'ECDSA', hash: 'SHA-256' },
      record.privateKey,
      bytes as unknown as ArrayBuffer,
    );
    return bytesToBase64Url(new Uint8Array(sig));
  }

  async deleteSigningKeyPair(keyId: PublicKeyId): Promise<void> {
    const db = await openSignDb();
    try {
      const tx = db.transaction(SIGN_STORE, 'readwrite');
      tx.objectStore(SIGN_STORE).delete(keyId);
      await txDone(tx);
    } finally {
      db.close();
    }
  }
}

// ---------------------------------------------------------------------------
// Singletons (the module's public surface; re-exported via crypto/index.ts)
// ---------------------------------------------------------------------------

export const keyManager: KeyManager = new WebCryptoKeyManager();
export const fingerprintCodec: FingerprintCodec = new WebCryptoFingerprintCodec();
export const signingKeyManager: SigningKeyManager = new WebCryptoSigningKeyManager();

export {
  // for tests / direct use
  base64UrlToBytes,
  bytesToBase64Url,
};
